var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CardList_1;
import React, { Children, Component } from 'react';
import { LayoutGap, SelectionType } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { arrayOf, bool, element, eventListener, expression, number, object, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ClickEvent, SelectItemEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, isFunction, isMobile, isValidWidth, propagateProps, trigger } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as AddIcon } from '../../../resources/assets/components/DLS_PlusSolid.svg';
import { CardProperties } from '../card/card-types.js';
import { Card } from '../card/card.js';
import { Loader } from '../loader/loader.js';
import { NotFound } from '../not-found/not-found.js';
import { CardListAPI } from './card-list-api.js';
import './card-list.css';
const ListContainer = {
    acceptedTypes: [
        ComponentType.DATA_DISPLAY,
    ],
    name: '',
};
function ListItem({ children, className }) {
    const names = classNames({
        'o-card-list__item': true,
        'u-col-3@lg': true,
        'u-col-4': true,
        [`${className}`]: Boolean(className),
    });
    return React.createElement("li", { className: names }, children);
}
ListItem.propTypes = {
    children: oneOfType([element, arrayOf(element)]),
    className: string,
};
let CardList = CardList_1 = class CardList extends Component {
    constructor() {
        super(...arguments);
        this.onCardSelect = (selected, card) => {
            const { onSelectItem, onUnselectItem } = this.props;
            const eventCallback = (selected) ? onSelectItem : onUnselectItem;
            isFunction(eventCallback) && eventCallback(new SelectItemEvent({ item: card }));
        };
    }
    renderEmptyMessage() {
        const { data, emptyResultSubtitleExpression, emptyResultTitleExpression, loading, showAddNew = CardList_1.defaultProps.showAddNew, } = this.props;
        if (data.length || loading || showAddNew) {
            return null;
        }
        return (React.createElement(NotFound, { className: "c-card-list__not-found", subtitleExpression: emptyResultSubtitleExpression, titleExpression: emptyResultTitleExpression }));
    }
    renderLoading() {
        const { loading } = this.props;
        if (!loading) {
            return null;
        }
        return (React.createElement(Loader, { centered: true, className: "c-card-list__loader u-hide@empty", size: isMobile() ? 32 : 64 }));
    }
    renderAddNew() {
        const { addNewClassName = CardList_1.defaultProps.addNewClassName, addNewExpression = CardList_1.defaultProps.addNewExpression, onAddNewClick, selectionType = CardList_1.defaultProps.selectionType, showAddNew, } = this.props;
        if (!showAddNew) {
            return null;
        }
        const className = classNames({
            'u-grid-col-4': true,
            [`${addNewClassName}`]: Boolean(addNewClassName),
        });
        return (React.createElement(Card, { className: className, onClick: onAddNewClick, selectionType: selectionType, uuid: "card-list-add-new", width: 0 },
            React.createElement("div", { className: "c-card-list__new-card" },
                React.createElement(AddIcon, { className: "c-card-list__new-card-icon" }),
                React.createElement("span", { className: "c-card-list__new-card-text" }, MessageService.translate(addNewExpression)))));
    }
    renderPlaceholderCards() {
        const { placeholderCount = CardList_1.defaultProps.placeholderCount, children } = this.props;
        const defaultCardProps = {
            props: {
                className: '',
                width: CardProperties.width.defaultValue,
            },
        };
        const [card = defaultCardProps] = Children.toArray(children);
        const { width, className } = card.props || {};
        const phClassName = classNames({
            'c-card-list__placeholder': true,
            'u-hide@sm': true,
            'u-hide@xsm': true,
            [`${className}`]: Boolean(className),
            [`u-grid-col-${width}`]: isValidWidth(width),
        });
        return Array.from({ length: placeholderCount }).map((_, index) => (React.createElement("div", { className: phClassName, key: `placeholder_${index}` })));
    }
    renderCards() {
        const { children, data, keyField, onClick, onContextMenuClick, onDoubleClick, selectable, selectedCards = CardList_1.defaultProps.selectedCards, selectionType, showDates = CardList_1.defaultProps.showDates, uuid, } = this.props;
        if (!Children.count(children)) {
            return (React.createElement(Container, { acceptedTypes: ListContainer.acceptedTypes, className: "c-card-list__container u-grid-col-12", repeated: true, uuid: uuid }));
        }
        return data.map((card, index) => {
            const selected = keyField && selectedCards.some(key => card[keyField] === key);
            const inheritedProps = {
                ...card,
                dateVisible: showDates,
                identifier: index,
                onClick: trigger(onClick, new ClickEvent({ value: card })),
                onDoubleClick,
                onMenuClick: onContextMenuClick,
                onSelect: ({ selected }) => this.onCardSelect(selected, card),
                selectable,
                selected,
                selectionType,
            };
            return propagateProps(children, inheritedProps, true);
        });
    }
    getStyle() {
        const { rowGap = CardList_1.defaultProps.rowGap, columnGap = CardList_1.defaultProps.columnGap, style = CardList_1.defaultProps.style, } = this.props;
        return {
            '--col-gap': columnGap,
            '--row-gap': rowGap,
            ...style,
        };
    }
    renderCardsContainer() {
        const { className, loading, noWrap = CardList_1.defaultProps.noWrap, data, } = this.props;
        if (!data.length && loading) {
            return null;
        }
        const names = classNames({
            'o-grid-card-list': true,
            'o-grid-card-list--no-wrap': noWrap,
            [`${className}`]: Boolean(className),
        });
        return (React.createElement("div", { className: names, style: this.getStyle() },
            this.renderAddNew(),
            this.renderCards(),
            this.renderPlaceholderCards()));
    }
    render() {
        const { loading = CardList_1.defaultProps.loading, showDates = CardList_1.defaultProps.showDates, } = this.props;
        const rootClassName = classNames({
            'c-card-list': true,
            'c-card-list--no-date': !showDates,
            'is-loading': loading,
        });
        return (React.createElement("div", { className: rootClassName },
            this.renderEmptyMessage(),
            this.renderCardsContainer(),
            this.renderLoading()));
    }
};
CardList.propTypes = {
    addNewClassName: string,
    addNewExpression: expression,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    columnGap: oneOf(enumValues(LayoutGap)).group('layout'),
    data: arrayOf(object).notEnumerable(),
    emptyResultSubtitleExpression: expression,
    emptyResultTitleExpression: expression,
    keyField: string,
    loading: bool,
    noWrap: bool,
    onAddNewClick: eventListener,
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onSelectItem: eventListener,
    onUnselectItem: eventListener,
    placeholderCount: number,
    rowGap: oneOf(enumValues(LayoutGap)).group('layout'),
    selectable: bool.notEnumerable(),
    selectedCards: oneOfType([arrayOf(string), arrayOf(number)]).notEnumerable(),
    selectionType: oneOf(enumValues(SelectionType)).notEnumerable(),
    showAddNew: bool,
    showDates: bool,
    style: object.notEnumerable(),
    uuid: string,
};
CardList.defaultProps = {
    addNewClassName: 'u-grid-col-3@lg',
    addNewExpression: 'tws.card.add-new',
    columnGap: LayoutGap.LARGE,
    data: [],
    loading: false,
    noWrap: false,
    placeholderCount: 0,
    rowGap: LayoutGap.LARGE,
    selectedCards: [],
    selectionType: SelectionType.NONE,
    showAddNew: true,
    showDates: true,
    style: {},
};
CardList = CardList_1 = __decorate([
    AwesomeComponent({ api: CardListAPI, containers: [ListContainer], type: ComponentType.LIST })
], CardList);
export { CardList, };
export default CardList;
//# sourceMappingURL=card-list.js.map